import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { QueryStatus, authAxios } from "../../utils";
import { RootState } from "../../redux/store";
import {
  DriversList,
  DriverApi,
  UpdateDriverPhoneInput,
  SendSmsPayload,
  RelatedDriverRequestsListApi,
  UpdateRelatedDriverRequestInput,
} from "./driversApi";

export interface DriverState {
  driversList: DriversList;
  driversListStatus: QueryStatus;
  updateDriverPhoneStatus: QueryStatus;
  sendSmsStatus: QueryStatus;
  relatedDriverRequestsList: RelatedDriverRequestsListApi[];
  relatedDriverRequestsListStatus: QueryStatus;
  updateRelatedDriverRequestStatus: QueryStatus;
}

const initialStateDriver: DriverState = {
  driversList: [],
  driversListStatus: "idle",
  updateDriverPhoneStatus: "idle",
  sendSmsStatus: "idle",
  relatedDriverRequestsList: [],
  relatedDriverRequestsListStatus: "idle",
  updateRelatedDriverRequestStatus: "idle",
};

export const driverListAsync = createAsyncThunk(
  "driverList/call",
  async (payload: { companyUuid: string }) => {
    const axios = authAxios();
    const response = await axios.get<DriverApi[]>(
      `/drivers/company/${payload.companyUuid}`,
    );
    return response.data.sort((a, b) => a.lastName.localeCompare(b.lastName));
  },
);

export const relatedDriverRequestsListAsync = createAsyncThunk(
  "relatedDriverRequestsList/call",
  async () => {
    const axios = authAxios();
    const response = await axios.get<RelatedDriverRequestsListApi[]>(
      `/pending_related_driver_requests`,
    );
    return response.data;
  },
);

export const updateRelatedDriverRequestAsync = createAsyncThunk(
  "updateRelatedDriverRequest/call",
  async (payload: UpdateRelatedDriverRequestInput) => {
    const axios = authAxios();
    await axios.post(`/update_related_driver_requests`, payload);
  },
);

export const updateDriverPhoneAsync = createAsyncThunk(
  "driverList/update",
  async (payload: UpdateDriverPhoneInput) => {
    const axios = authAxios();
    await axios.post("/drivers/update_phone", payload);
  },
);

export const sendSmsAsync = createAsyncThunk(
  "driverList/sendSms",
  async (payload: SendSmsPayload) => {
    const axios = authAxios();
    await axios.post("/drivers/send_sms", payload);
  },
);
export const driverSlice = createSlice({
  name: "drivers",
  initialState: initialStateDriver,
  reducers: {
    resetSendSmsStatus: (state) => {
      state.sendSmsStatus = "idle";
    },
    resetUpdateRelatedDriverRequestStatus: (state) => {
      state.updateRelatedDriverRequestStatus = "idle";
    },
    resetDriversListStatus: (state) => {
      state.driversListStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(driverListAsync.pending, (state) => {
        state.driversListStatus = "processing";
      })
      .addCase(driverListAsync.fulfilled, (state, action) => {
        state.driversList = action.payload;
        state.driversListStatus = "success";
      })
      .addCase(driverListAsync.rejected, (state) => {
        state.driversListStatus = "failed";
      })
      .addCase(updateDriverPhoneAsync.pending, (state) => {
        state.updateDriverPhoneStatus = "processing";
      })
      .addCase(updateDriverPhoneAsync.fulfilled, (state) => {
        state.updateDriverPhoneStatus = "success";
        state.driversListStatus = "idle";
      })
      .addCase(updateDriverPhoneAsync.rejected, (state) => {
        state.updateDriverPhoneStatus = "failed";
        state.driversListStatus = "idle";
      })
      .addCase(sendSmsAsync.pending, (state) => {
        state.sendSmsStatus = "processing";
      })
      .addCase(sendSmsAsync.fulfilled, (state) => {
        state.sendSmsStatus = "success";
      })
      .addCase(sendSmsAsync.rejected, (state) => {
        state.sendSmsStatus = "failed";
      })
      .addCase(relatedDriverRequestsListAsync.pending, (state) => {
        state.relatedDriverRequestsListStatus = "processing";
      })
      .addCase(relatedDriverRequestsListAsync.fulfilled, (state, action) => {
        state.relatedDriverRequestsList = action.payload;
        state.relatedDriverRequestsListStatus = "success";
      })
      .addCase(relatedDriverRequestsListAsync.rejected, (state) => {
        state.relatedDriverRequestsListStatus = "failed";
      })
      .addCase(updateRelatedDriverRequestAsync.pending, (state) => {
        state.updateRelatedDriverRequestStatus = "processing";
      })
      .addCase(updateRelatedDriverRequestAsync.fulfilled, (state) => {
        state.updateRelatedDriverRequestStatus = "success";
        state.relatedDriverRequestsListStatus = "idle";
      })
      .addCase(updateRelatedDriverRequestAsync.rejected, (state) => {
        state.updateRelatedDriverRequestStatus = "failed";
      });
  },
});

export const selectDriversList = (state: RootState) =>
  state.drivers.driversList;
export const selectDriversListStatus = (state: RootState) =>
  state.drivers.driversListStatus;
export const selectUpdateDriverPhoneStatus = (state: RootState) =>
  state.drivers.updateDriverPhoneStatus;
export const selectSendSmsStatus = (state: RootState) =>
  state.drivers.sendSmsStatus;
export const selectRelatedDriverRequestsList = (state: RootState) =>
  state.drivers.relatedDriverRequestsList;
export const selectRelatedDriverRequestsListStatus = (state: RootState) =>
  state.drivers.relatedDriverRequestsListStatus;
export const selectUpdateRelatedDriverRequestStatus = (state: RootState) =>
  state.drivers.updateRelatedDriverRequestStatus;

export default driverSlice.reducer;
